import { ChangeEvent, useState, memo, InputHTMLAttributes, useEffect, useMemo } from "react";
import { captureException } from "@sentry/nextjs";
import { usePathname } from "next/navigation";
import CloseIcon from "components/icons-svg/CloseIcon";
import GrMapLocationIcon from "components/icons-svg/GrMapLocationIcon";
import NewLocationIcon from "components/icons-svg/NewLocationIcon";
import { autoCompleteAPI, iconMap, NewDataType } from "components/searchBar/SearchBarInput";
import SearchPlaceSuggestions from "components/searchBar/SearchPlaceSuggestions";
import SuggestionListWithFallback from "components/searchBar/SuggestionListWithFallback";
import { useDebounce } from "hooks/use-debounce";
import { getGeoCodesByPlaceId, Status } from "services/api/googleAPIs";
import { selectUser } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markLocalSearch, selectLocalSearchLocation } from "stores/local-search/localSearchSlice";
import { selectLocationName } from "stores/save-search/saveSearchSlice";
import { markSearch, selectWithPolygon } from "stores/search/searchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow } from "stores/ui/uiSlice";
import { isDesktop, waitForGoogleMaps } from "utils/dom";
import { processPlaceDescription, stringifyBounds } from "utils/helper";
const SearchLocationInputWithoutMemo = ({
  haveIcon = false,
  isSearchCleared,
  setIsSearchCleared,
  ...rest
}: {
  haveIcon?: boolean;
  isSearchCleared: boolean;
  setIsSearchCleared: (value: boolean) => void;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const dispatch = useAppDispatch();
  const localSearchLocation = useAppSelector(selectLocalSearchLocation); // local search location data
  // const localSearchDates = useAppSelector(selectLocalSearchDateRange) // local search date range data
  const isWithPolygon = useAppSelector(selectWithPolygon); // is polygon visible
  const user = useAppSelector(selectUser);
  const pathname = usePathname();
  // Determine if the current page is a regional page.
  const isRegionalPage = pathname?.startsWith("/locations");

  // Initialize state variables.
  const [init, setInit] = useState(false);
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState("");
  const [inputText, setInputText] = useState<string>(localSearchLocation?.shortName || "");
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const debouncedInputText = useDebounce(inputText || "", 250); // Debounce the input text to reduce unnecessary API calls.

  const locationName = useAppSelector(selectLocationName);
  useEffect(() => {
    if (isRegionalPage) {
      setAddressData(locationName);
      dispatch(markSearch({
        shortName: locationName,
        longName: locationName
      }));
    }
  }, [dispatch, isRegionalPage, locationName]);
  // This useEffect triggers when the user clicks the 'Clear all' button in the drawer [mobile & tablet screen].
  // It resets all search-related state variables to their initial values.
  useEffect(() => {
    if (isSearchCleared) {
      setInputText("");
      setAddress("");
      setAddressData("");
      setIsSearchCleared(false); // Reset the 'isSearchCleared' state variable to false.
    }
  }, [isSearchCleared]);

  // Function to handle search flow transitions.
  const handleSearchFlow = (currentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab
    }));
  };
  // State for storing address predictions from the autocomplete API.
  const [addressList, setAddressList] = useState<google.maps.places.AutocompletePrediction[]>([]);

  // Memoized value for the input field's value.
  const inputValue = useMemo(() => {
    if (init) {
      return address || inputText;
    }
    if (localSearchLocation.isSearchingInMap && !isWithPolygon) {
      return "Map area";
    }
    return localSearchLocation?.longName || addressData;
  }, [address, addressData, init, inputText, localSearchLocation.isSearchingInMap, localSearchLocation?.longName, isWithPolygon]);
  // Function to handle the focus event on the input field.
  const onFocus = () => {
    handleSearchFlow("location");
  };
  // Function to determine what should be the next open search tab.
  const whatShouldBeOpenNext = (name: string | null) => {
    if (!isDesktop() && name !== null) {
      return dispatch(markSearchFlow({
        currentSearchTab: "dates"
      }));
    }
  };
  // Function to handle the selection of a place from the autocomplete results.
  const onPlaceSelected = (place: NewDataType) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    const getGeocodeFromPlaceId = async (placeId: string) => {
      const data = await getGeoCodesByPlaceId(placeId);
      return data?.data;
    };
    if (!place.description) return;

    // Optimistically opening the next field, to prevent CLS due to late layout shift if triggered after getGeocodeFromPlaceId call, the .catch() will handle the error
    whatShouldBeOpenNext(place.description);
    getGeocodeFromPlaceId(place?.place_id).then(res => {
      if (res == null || res?.status !== Status.OK) {
        return;
      }
      const selectedPlace = res?.results[0];
      let bounds: string | null = null;
      if (selectedPlace?.geometry?.bounds || selectedPlace?.geometry?.viewport) {
        const temp = selectedPlace?.geometry?.viewport || selectedPlace?.geometry?.bounds;
        const ne = temp?.northeast;
        const sw = temp?.southwest;
        if (ne && sw) {
          bounds = stringifyBounds({
            se: {
              lat: sw.lat,
              lng: ne.lng
            },
            nw: {
              lat: ne.lat,
              lng: sw.lng
            },
            sw: sw,
            ne: ne
          });
        }
      }
      const lat: number = selectedPlace.geometry.location.lat;
      const lng: number = selectedPlace.geometry.location.lng;
      const placeId = selectedPlace?.place_id;
      const placeType = selectedPlace.types?.[0] || selectedPlace.geometry?.location_type;
      setAddressList([]);
      setAddressData(place?.description);
      setAddress("");
      setInit(false);
      setHasLoadedSuggestions(false);
      dispatch(markLocalSearch({
        locationData: {
          lat,
          lng,
          placeId,
          placeType,
          shortName: processPlaceDescription(place.description || ""),
          longName: place.description,
          page: 0,
          bounds,
          isSearchingInMap: false,
          propertyConfigTypeId: null,
          isBoundaryVisible: true,
          isBoundaryAutoRemoved: false
        },
        searchAddressResult: {
          address_components: selectedPlace?.address_components
        }
      }));
      whatShouldBeOpenNext(place.description);
      return null;
    }).catch(e => {
      dispatch(markLocalSearch({
        locationData: {
          shortName: processPlaceDescription(place.description || ""),
          longName: place.description,
          page: 0,
          bounds: null,
          isSearchingInMap: false,
          propertyConfigTypeId: null
        }
      }));
      whatShouldBeOpenNext(null);
      setAddress("");
      setAddressList([]);
      console.error(e);
      captureException(new Error(`Error 'getGeocode' ${JSON.stringify(e)}`));
    });

    // user identify mixpanel
    if (user && user.userId) {
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        if (user && user.userId) {
          mixpanelV1Client.IDENTIFY(user.userId);
          mixpanelV2Client.IDENTIFY(user.userId);
        }
      })();
    }
  };
  useEffect(() => {
    if (!debouncedInputText) {
      setAddressList([]);
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(false);
      return;
    }
    setIsLoadingSuggestions(true);
    const displaySuggestions = function (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) {
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(true);
      if (status != google.maps.places.PlacesServiceStatus.OK) {
        setAddressList([]);
        return;
      }
      const filteredSuggestions = predictions?.filter(suggestion => {
        return suggestion.types && suggestion.types.indexOf("restaurant") === -1 && suggestion.types.indexOf("food") === -1 && suggestion.types.indexOf("lodging") === -1 && suggestion.types.indexOf("university") === -1 && suggestion.types.indexOf("dentist") === -1 && suggestion.types.indexOf("doctor") === -1 && suggestion.types.indexOf("health") === -1 && suggestion.types.indexOf("hospital") === -1 && suggestion.types.indexOf("premise") === -1 && suggestion.types.indexOf("shopping_mall") === -1 && suggestion.types.indexOf("airport") === -1 && suggestion.types.indexOf("church") === -1 && suggestion.types.indexOf("store") === -1;
      }) || [];
      setAddressList(filteredSuggestions);
    };
    // Call the autocomplete API with the debounced input value.
    waitForGoogleMaps(() => {
      autoCompleteAPI(debouncedInputText, displaySuggestions);
    });
  }, [debouncedInputText]);

  // Handle the input change event
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    setAddress(e.target.value || "");
    setInputText(e.target.value || "");
    setInit(true);
  };
  return <div className='relative' data-sentry-component="SearchLocationInputWithoutMemo" data-sentry-source-file="SearchLocationInput.tsx">
      <div className='relative mb-4 lg:mb-0'>
        {/* Display the location icon if the 'haveIcon' prop is true */}
        {haveIcon && <div className='absolute left-4 flex h-12 items-center text-primary-500'>
            <NewLocationIcon className='text-primary-500' />
          </div>}
        <input aria-label='search options' onFocus={() => {
        // Note: Below line was not letting the modal to close when drawer was open (820 x 1180).
        onFocus();
      }} autoComplete='off' name='addressinput' type='search' value={inputValue} onChange={handleInput} // Handle changes to the input value.
      onClick={() => {
        handleSearchFlow("location");
      }} className={`google-autocomplete-places h-12 w-full min-w-[125px] text-ellipsis rounded-[100px] border border-Gray-400 bg-white py-3 pr-10 text-base font-semibold leading-4 text-brand-dark shadow-[0px_4px_4px_0px_#0000000A] placeholder:text-base placeholder:font-semibold placeholder:text-Gray-500 focus-visible:outline-0 ${haveIcon ? " pl-[34px]" : " pl-3"} `} placeholder='Ex: “Seattle” or “Texas” or “Italy”' {...rest} />
        {/* Close button for clearing the search input */}
        <div className='absolute right-4 top-0 flex h-12 items-center text-grayText'>
          <button className='btn h-auto min-w-0 p-1' aria-label='mobile clear search location' onClick={() => {
          dispatch(markLocalSearch({
            locationData: {
              lat: null,
              lng: null,
              placeId: null,
              placeType: null,
              shortName: null,
              longName: null,
              bounds: null,
              isSearchingInMap: false
            },
            searchAddressResult: null
          }));
          setInputText("");
          setAddress("");
          setAddressData("");
          setHasLoadedSuggestions(false);
        }}>
            <CloseIcon className='h-5 w-5 fill-textColor' data-sentry-element="CloseIcon" data-sentry-source-file="SearchLocationInput.tsx" />
          </button>
        </div>
      </div>
      {/* Display address suggestions if there are any */}
      {inputValue === "" ? <SearchPlaceSuggestions fromModal={true} variant='homeNew' /> : (hasLoadedSuggestions || addressList.length > 0) && <main className='overflow-hidden bg-white'>
            <SuggestionListWithFallback addressList={addressList} onSelect={onPlaceSelected} iconMap={iconMap} defaultIcon={GrMapLocationIcon} variant='mobile' isLoading={isLoadingSuggestions} hasLoaded={hasLoadedSuggestions} />
          </main>}
    </div>;
};
const SearchLocationInput = memo(SearchLocationInputWithoutMemo);
export default SearchLocationInput;